import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  X,
  Play,
  Target,
  ClipboardList,
  Award,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Map notification types to icons
const notificationIcons = {
  campaign_activated: Play,
  objectives_submitted: Target,
  objectives_validated: CheckCircle,
  objectives_rejected: XCircle,
  evaluation_pending: ClipboardList,
  evaluation_completed: Award,
  feedback_received: MessageSquare,
  deadline_reminder: AlertTriangle,
  action_validation_required: Target,
  action_validated: CheckCircle,
  plan_created: Target
};

// Map notification types to colors
const notificationColors = {
  campaign_activated: 'text-green-500 bg-green-50',
  objectives_submitted: 'text-blue-500 bg-blue-50',
  objectives_validated: 'text-green-500 bg-green-50',
  objectives_rejected: 'text-red-500 bg-red-50',
  evaluation_pending: 'text-orange-500 bg-orange-50',
  evaluation_completed: 'text-purple-500 bg-purple-50',
  feedback_received: 'text-blue-500 bg-blue-50',
  deadline_reminder: 'text-orange-500 bg-orange-50',
  action_validation_required: 'text-amber-500 bg-amber-50',
  action_validated: 'text-green-500 bg-green-50',
  plan_created: 'text-purple-500 bg-purple-50'
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications count on mount and periodically
  useEffect(() => {
    if (user?.id && user?.companyId) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id, user?.companyId]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get(`/notifications/count?user_id=${user?.id}&company_id=${user?.companyId}`);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/notifications?user_id=${user?.id}&company_id=${user?.companyId}&limit=20`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const markAsRead = async (notificationId, removeFromList = false) => {
    try {
      await api.put(`/notifications/${notificationId}/read?user_id=${user?.id}`);
      
      if (removeFromList) {
        // Remove notification from list immediately
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      } else {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      }
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put(`/notifications/mark-all-read?user_id=${user?.id}&company_id=${user?.companyId}`);
      // Clear all notifications from list
      setNotifications([]);
      setUnreadCount(0);
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Erreur lors du marquage');
    }
  };

  const dismissNotification = async (e, notificationId) => {
    e.stopPropagation(); // Prevent triggering the notification click
    try {
      await api.put(`/notifications/${notificationId}/read?user_id=${user?.id}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const archiveNotification = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/archive?user_id=${user?.id}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read and remove from list
    if (!notification.read) {
      markAsRead(notification.id, true);
    }
    
    // Navigate to link if present
    if (notification.link) {
      setIsOpen(false);
      window.location.href = notification.link;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)}j`;
    
    return date.toLocaleDateString('fr-FR');
  };

  const IconComponent = (type) => {
    const Icon = notificationIcons[type] || Bell;
    return Icon;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full transform translate-x-1 -translate-y-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs h-7"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Tout marquer lu
                </Button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-webtech-blue border-t-transparent rounded-full" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <BellOff className="w-10 h-10 mb-2" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => {
                  const Icon = IconComponent(notification.type);
                  const colorClass = notificationColors[notification.type] || 'text-slate-500 bg-slate-50';
                  
                  return (
                    <div
                      key={notification.id}
                      className={`flex items-start gap-3 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                        !notification.read ? 'bg-blue-50/30' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {/* Icon */}
                      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-600'}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatTime(notification.createdAt)}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        )}
                        <button
                          onClick={(e) => dismissNotification(e, notification.id)}
                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer cette notification"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
              <Button
                variant="ghost"
                className="w-full text-sm text-webtech-blue hover:text-webtech-blue-hover"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/notifications';
                }}
              >
                Voir toutes les notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
